/* eslint-disable react/prop-types */
import React, { forwardRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useMergeState } from 'irmlib/hooks'
import { useTheme } from 'irmlib/styles'
import MaterialTable, { MTableBody, MTableBodyRow } from 'material-table'
import AddBox from '@material-ui/icons/AddBox'
import ArrowDownward from '@material-ui/icons/ArrowDownward'
import Check from '@material-ui/icons/Check'
import ChevronLeft from '@material-ui/icons/ChevronLeft'
import ChevronRight from '@material-ui/icons/ChevronRight'
import Clear from '@material-ui/icons/Clear'
import DeleteOutline from '@material-ui/icons/DeleteOutline'
import Edit from '@material-ui/icons/Edit'
import FilterList from '@material-ui/icons/FilterList'
import FirstPage from '@material-ui/icons/FirstPage'
import LastPage from '@material-ui/icons/LastPage'
import Remove from '@material-ui/icons/Remove'
import SaveAlt from '@material-ui/icons/SaveAlt'
import Search from '@material-ui/icons/Search'
import ViewColumn from '@material-ui/icons/ViewColumn'
import { roundNumber } from '../../../irmlib/src/common'
import { rememberSelection } from '../../../services/firestore/index'
import { getUser } from '../../../redux/global'
import { useTranslation } from 'react-i18next'
// import { indexOf } from 'lodash'

const tableIcons = {
  Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => (
    <ChevronRight {...props} ref={ref} />
  )),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => (
    <ChevronLeft {...props} ref={ref} />
  )),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowDownward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
}

const initialSate = {
  page: 0,
  pageData: [],
  rowsPerPageOptions: [10, 20, 50, 100],
  pageSize: 20,
  selectedRow: ''
}

const BaseTable = (props) => {
  const { t } = useTranslation()
  const [state, setState] = useMergeState(initialSate)
  let rowSelected = -1
  const theme = useTheme()
  const { reportsTable, draggable } = props

  useEffect(() => {
    const doGet = async () => {
      const data = await props.getPageData(props.title)
      setState({ pageData: data })
    }
    doGet()
  }, [props.title, props.refresh])

  const handleSelectionChange = (rows, row) => {
    props.onSelectionChange(rows, row)
  }

  const DragState = {
    row: -1,
    dropIndex: -1
  }

  const offsetIndex = (from, to, arr = []) => {
    if (from < to) {
      const start = arr.slice(0, from)
      const between = arr.slice(from + 1, to + 1)
      const end = arr.slice(to + 1)
      return [...start, ...between, arr[from], ...end]
    }
    if (from > to) {
      const start = arr.slice(0, to)
      const between = arr.slice(to, from)
      const end = arr.slice(from + 1)
      return [...start, arr[from], ...between, ...end]
    }
    return arr
  }

  const reOrderRow = (from, to) => {
    const newtableData = offsetIndex(from, to, props.tableRef.current.dataManager.data)
    props.tableRef.current.dataManager.setData(newtableData)
    props.tableRef.current.setState(props.tableRef.current.dataManager.getRenderState())
  }

  const handleRowSelect = (event, propsData) => {
    rowSelected = propsData.data.tableData.id
    props.tableRef.current.setState(props.tableRef.current.dataManager.getRenderState())
  }

  const getNumber = (number) => {
    if (number) {
      return parseFloat(number)
    } else {
      return 0
    }
  }

  const getReportsGrandTotal = (data) => {
    const grandTotalText = t('reports.grandTotal')
    const grandTotal = {
      totalRecordCount: 0,
      rowType: grandTotalText,
      workerId: grandTotalText,
      jobId: grandTotalText,
      dataEntryKeyStrokes: 0,
      verificationKeyStrokes: 0,
      keyStrokeCount: 0,
      totalBoxCount: 0,
      keyedTime: 0,
      totalVerifyTime: 0,
      kphTime: 0,
      filesByHour: 0,
      paidTime: 0,
      gdeTime: 0,
      kphGde: 0,
      tableData: { id: 'grandTotal' }
    }
    data?.forEach(data => {
      grandTotal.keyStrokeCount = roundNumber(grandTotal.keyStrokeCount + getNumber(data.keyStrokeCount))
      grandTotal.totalBoxCount = roundNumber(grandTotal.totalBoxCount + getNumber(data.totalBoxCount))
      grandTotal.totalRecordCount = roundNumber(grandTotal.totalRecordCount + getNumber(data.totalRecordCount))
      grandTotal.keyedTime = roundNumber(grandTotal.keyedTime + getNumber(data.keyedTime))
      grandTotal.kphTime = roundNumber(grandTotal.kphTime + getNumber(data.kphTime))

      // employee level reports
      if (reportsTable?.isEmployeeLevel) {
        grandTotal.dataEntryKeyStrokes = roundNumber(grandTotal.dataEntryKeyStrokes + getNumber(data.dataEntryKeyStrokes))
        grandTotal.verificationKeyStrokes = roundNumber(grandTotal.verificationKeyStrokes + getNumber(data.verificationKeyStrokes))
        grandTotal.paidTime = roundNumber(grandTotal.paidTime + getNumber(data.paidTime))
        grandTotal.gdeTime = roundNumber(grandTotal.gdeTime + getNumber(data.gdeTime))
        grandTotal.kphGde = roundNumber(grandTotal.kphGde + getNumber(data.kphGde))
      } else {
        grandTotal.filesByHour = roundNumber(grandTotal.filesByHour + getNumber(data.filesByHour))
        grandTotal.totalVerifyTime = roundNumber(grandTotal.totalVerifyTime + getNumber(data.totalVerifyTime))
      }
    })
    return grandTotal
  }

  const onChangeColumnsHidden = (column, hidden) => {
    const hiddenColumns = []
    const displayColumnsId = []
    props.tableRef.current.props.columns.forEach((column) => {
      if (column.hidden) {
        hiddenColumns.push(column)
      } else {
        displayColumnsId.push(column.field)
      }
    })
    
    if (props.reportFor === 'CustomerLevel') {
      window.sessionStorage.setItem('customerLevelReportDisplayColumnsId', displayColumnsId)
      rememberSelection(getUser().workerId, { customerLevelReportDisplayColumnsId: displayColumnsId })
    }
  }

  if (props.reportFor === 'CustomerLevel' && window.sessionStorage.getItem('customerLevelReportDisplayColumnsId') !== 'undefined') {
    const displayColumns = window.sessionStorage.getItem('customerLevelReportDisplayColumnsId')
    props?.tableRef?.current?.props?.columns.forEach((column) => {
      if (!displayColumns.includes(column.field)) {
        column.hidden = true
        column.hiddenByColumnsButton = true
      }
    })
  }

  // backend data conversion for job status
  state.pageData.forEach((element, index) => {
    if (element?.boxId !== undefined && element?.status && element?.status1) {
      element.status = t(`boxStatusObj.${element?.status1}`)
    } else if (element?.status && element?.status1) {
      element.status = t(`jobStatusObj.${element?.status1}`)
    }

    if (element.jobType && element?.jobType1) {
      element.jobType = t(`templateTypeObj.${element?.jobType1}`)
    }

    if (element?.exceptionCode && element?.exceptionCode1) {
      element.exceptionCode = t(`exceptionCodesObj.${element.exceptionCode1}`)
    }
  })

  // table header conversion
  if (props?.reportFor === 'CustomerLevel' || props?.reportFor === 'EmployeeLevel') {
    props.columns.forEach((element, index) => {
      element.title = t(`${element?.title1}`)
    })
  }
  const columnData = props.columns

  let showSelectAllCheckbox = true
  if (props.showSelectAllCheckbox !== undefined) {
    showSelectAllCheckbox = props.showSelectAllCheckbox
  }

  return (
    <MaterialTable
      icons={tableIcons}
      tableRef={props.tableRef}
      columns={columnData}
      actions={props.actions}
      localization={{
        header: { actions: '' }
      }}
      options={
        {
          headerStyle: {
            fontWeight: 'bold'
          },
          maxBodyHeight: props.maxHeight || '500px',
          rowStyle: rowData => {
            return {
              backgroundColor: (rowSelected === rowData.tableData.id)
                ? theme.palette.table.clickOnRow
                : (rowData.rowSelected === 'rowSelected') ? theme.palette.table.selectedRow : theme.palette.table.normalRow,
              fontWeight: (rowData?.rowType === t('reports.grandTotal')) ? 'bold' : 'normal'
            }
          },
          fixedColumns: {
            left: 0,
            right: 0
          },
          selection: props.selection,
          search: props.search,
          showTitle: false,
          toolbar: props.toolbar,
          showTextRowsSelected: false,
          exportButton: props.exportButton,
          showSelectAllCheckbox: showSelectAllCheckbox,
          emptyRowsWhenPaging: false,
          columnsButton: true,
          paging: props.paging,
          sorting: props.sorting,
          validationError: true,
          filtering: props.filtering,
          draggable: false,
          pageSize: 20,
          actionsColumnIndex: -1,
          pageSizeOptions: [20, 50, 100],
          selectionProps: (rowData) => ({
            disabled: (props.scModifyTemplate !== 'scModifyTemplate' && props.reportFor !== 'multiRequestSelection' && ((rowData.isRequired && !rowData.isRequiredChanged) || (getUser().role === 'USER')))
          }),
        }
      }
      onSelectionChange={(rows, row, e) => handleSelectionChange(rows, row, e)}
      onChangeColumnHidden={(column, hidden) => onChangeColumnsHidden(column, hidden)}
      data={state.pageData}
      title={props.title}
      
      components={{
        Row: rowProps => (
          <MTableBodyRow {...rowProps}
            onRowClick= {e => handleRowSelect(e, rowProps)} 
            draggable={draggable}
            onDragStart={(e) => {
              DragState.row = rowProps.data.tableData.id
            }}
            onDragEnter={e => {
              e.preventDefault()
              if (rowProps.data.tableData.id !== DragState.row) {
                DragState.dropIndex = rowProps.data.tableData.id
              }
            }}
            onDragEnd={(e) => {
              if (DragState.dropIndex !== -1) {
                reOrderRow(DragState.row, DragState.dropIndex)
              }
              DragState.row = -1
              DragState.dropIndex = -1
            }}
          />
        ),
        /* eslint-disable react/prop-types */
        Body: props => {
          if (reportsTable?.status) {
            const { renderData = [] } = props
            if (renderData.length > 0) {
              const existingTotal = props.renderData.filter(row => row?.rowType === t('reports.grandTotal'))
              const index = props.renderData.indexOf(existingTotal[0])
              if (index > -1) {
                props.renderData.splice(index, 1)
              }
              const grandTotal = getReportsGrandTotal(state.pageData)
              props.renderData.push(grandTotal)
            }
          }
          return (
            <>
              <MTableBody {...props} />
            </>
          )
        }
      }}
    />
  )
}

BaseTable.propTypes = {
  columns: PropTypes.array,
  actions: PropTypes.array,
  getPageData: PropTypes.func,
  paging: PropTypes.bool,
  onSelectionChange: PropTypes.func,
  search: PropTypes.bool,
  toolbar: PropTypes.bool,
  sorting: PropTypes.bool,
  filtering: PropTypes.bool,
  selection: PropTypes.bool,
  title: PropTypes.string,
  data: PropTypes.any,
  exportButton: PropTypes.bool,
  draggable: PropTypes.bool,
  tableRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any })
  ]),
  refresh: PropTypes.bool,
  reportsTable: PropTypes.object,
  showSelectAllCheckbox: PropTypes.bool,
  maxHeight: PropTypes.string
}

export default BaseTable
